import React, { useState } from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, History } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <div className={`text-sm ${bold ? "font-bold text-brand-text" : "text-slate-600"}`}>{label}</div>
    <div className={`text-sm ${bold ? "font-heading font-bold text-brand-red" : "text-brand-text"}`}>{value}</div>
  </div>
);

const HistoryPeriodCard = ({ p }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card className="rounded-2xl border-brand-border overflow-hidden" data-testid={`history-period-${p.period_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left">
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
            {p.status === "open" ? "Current" : "Pay period"}
          </div>
          <div className="font-heading font-bold text-brand-text text-sm truncate">{p.period_name}</div>
          <div className="text-[11px] text-slate-500">
            {p.days} day{p.days !== 1 ? "s" : ""} · {p.stops} stops · {p.parcels} parcels
            {p.mileage ? ` · ${Math.round(p.mileage)} mi` : ""}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-base font-bold text-brand-red">{gbp(p.net_pay || 0)}</div>
          <Badge variant="outline" className="mt-0.5 uppercase text-[9px] tracking-wider">
            {p.status}
          </Badge>
        </div>
      </button>
      {open && (
        <div className="border-t border-brand-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Route</th>
                <th className="text-right px-3 py-2">Stops</th>
                <th className="text-right px-3 py-2">Parcels</th>
                <th className="text-right px-3 py-2">Miles</th>
                <th className="text-right px-3 py-2">£</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {p.rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{ddmmyyyy(r.work_date)}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.route || "—"}</td>
                  <td className="px-3 py-1.5 text-right text-xs">{r.stops}</td>
                  <td className="px-3 py-1.5 text-right text-xs">{r.parcels}</td>
                  <td className="px-3 py-1.5 text-right text-xs text-slate-500">{r.mileage ? Number(r.mileage).toFixed(1) : "—"}</td>
                  <td className="px-3 py-1.5 text-right font-heading font-bold text-xs">{gbp(r.route_payment)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-slate-50 text-[11px] text-slate-600 flex items-center justify-between border-t border-slate-100">
            <span>Gross <b>{gbp(p.gross || 0)}</b> · Deductions <b>{gbp(p.deductions || 0)}</b></span>
            <span>Pays {ddmmyyyy(p.payment_date)}</span>
          </div>
        </div>
      )}
    </Card>
  );
};

const DriverPay = () => {
  const { data, isLoading } = useSWR("/driver-portal/me", fetcher);
  const { data: history } = useSWR("/driver-portal/me/history", fetcher);
  if (isLoading || !data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  const { period, summary } = data;

  return (
    <div className="p-4 pt-6 space-y-4">
      <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Payment breakdown</div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-brand-text mt-1">Your pay</h1>

      {summary && (
        <Card className="p-5 rounded-2xl border-brand-border" data-testid="driver-pay-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{period?.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">Pays on {ddmmyyyy(period?.payment_date)}</div>

          <div className="mt-4 space-y-0">
            <Row label="Gross DPD earnings" value={gbp(summary.gross_dpd)} />
            <Row label="Base pay" value={gbp(summary.base_pay)} />
            {summary.vehicle_fee_total > 0 && <Row label="↳ Vehicle fee included" value={gbp(summary.vehicle_fee_total)} />}
            {summary.fuel_fee_total > 0 && <Row label="↳ Fuel fee included" value={gbp(summary.fuel_fee_total)} />}
            {summary.management_fee_total > 0 && <Row label="↳ Management fee included" value={gbp(summary.management_fee_total)} />}
            <Row label="Deductions" value={gbp(summary.deductions)} />
            <Row label="Net pay" value={gbp(summary.net_pay)} bold />
          </div>
        </Card>
      )}

      {!summary && <div className="text-sm text-slate-500 py-2">No open pay period.</div>}

      <div className="pt-4 flex items-center gap-2">
        <History className="w-4 h-4 text-brand-red" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-red font-mono">Your history</div>
        <div className="ml-auto text-[11px] text-slate-500">
          {history?.period_count || 0} period(s) · {history?.total_days || 0} day(s)
        </div>
      </div>

      <div className="space-y-2">
        {(history?.periods || []).map((p) => (
          <HistoryPeriodCard key={p.period_id} p={p} />
        ))}
        {(history?.periods || []).length === 0 && (
          <Card className="p-6 text-center text-sm text-slate-500 rounded-2xl">
            No historical pay data yet.
          </Card>
        )}
      </div>
    </div>
  );
};

export default DriverPay;
